import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import { buildArticleListSchema, type ArticleItem } from '@core-mkt/services/schema/article-schema.util';
import type { BlogEntry } from './entry-listings';

export interface EntryListingsSchemaData {
  /** Absolute URL of the current listing page — used to namespace the ItemList @id. */
  pageUrl: string;
  /** Site root for the Organization cross-reference and to build absolute article URLs. */
  siteUrl: string;
  /** Brand name used on the cross-referenced Organization. */
  organizationName: string;
  /** Optional ItemList title (typically the listing's `entryListingsTitle` from Craft). */
  listTitle?: string;
  /** Blog entries for the current page. */
  blogEntries: BlogEntry[] | null | undefined;
}

const VARIANT_ID_FRAGMENT = 'articles-listings';
const DEFAULT_LIST_NAME = 'Articles';

/**
 * Returns BlogPosting nodes + an ItemList for the entry-listings container.
 * Sources fields from each BlogEntry's Craft data — slug, title, blogSummary,
 * featuredEntryImage, postDate, blogCategories. Uses the first blog category
 * as `articleSection`.
 *
 * Returns null when no entries are available.
 */
export function getEntryListingsSchema(input: EntryListingsSchemaData | null): SchemaOrgNode[] | null {
  if (!input || !Array.isArray(input.blogEntries) || input.blogEntries.length === 0) return null;

  const items: ArticleItem[] = input.blogEntries.map((entry) => buildArticleItem(entry, input.siteUrl));

  return buildArticleListSchema({
    listId: `${input.pageUrl}#${VARIANT_ID_FRAGMENT}`,
    listName: input.listTitle?.trim() || DEFAULT_LIST_NAME,
    siteUrl: input.siteUrl,
    organizationName: input.organizationName,
    items,
  });
}

function buildArticleItem(entry: BlogEntry, siteUrl: string): ArticleItem {
  const url = buildArticleUrl(entry.slug, siteUrl);
  const image = entry.featuredEntryImage?.[0]?.url;
  const articleSection = entry.blogCategories?.[0]?.title;
  return {
    headline: entry.title ?? '',
    url,
    description: entry.blogSummary ?? undefined,
    image,
    datePublished: entry.postDate ?? entry.dateCreated ?? undefined,
    dateModified: entry.dateUpdated ?? undefined,
    articleSection,
  };
}

function buildArticleUrl(slug: string | undefined, siteUrl: string): string {
  if (!slug) return '';
  const root = siteUrl.endsWith('/') ? siteUrl : `${siteUrl}/`;
  const path = slug.replace(/--/g, '/');
  return `${root}${path}/`;
}
